import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { 
  Users, DollarSign, FileText, TrendingUp, Calendar, Search,
  RefreshCw, LogOut, BarChart3, ShoppingCart, Wallet, Clock,
  ChevronDown, ChevronUp, CreditCard, Filter, PieChart, LineChart as LineChartIcon
} from 'lucide-react';
import { Session } from '@supabase/supabase-js';
import { 
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, PieChart as RechartsPieChart, 
  Pie, Cell
} from 'recharts';

// Tipo para los datos del usuario
interface UserData {
  id: string;
  email: string;
  created_at: string;
}

// Tipo para las transacciones
interface Transaction {
  id: number;
  transaction_id: string;
  user_id: string;
  user_email?: string;
  amount: number;
  vcoins_amount: number;
  currency: string;
  status: string;
  created_at: string;
  description: string;
}

// Tipo para las métricas del dashboard
interface DashboardMetrics {
  totalUsers: number;
  totalSales: number;
  totalCVs: number;
  averageSpend: number;
  pendingTransactions: number;
  successfulTransactions: number;
  cancelledTransactions: number;
}

// Tipo para datos agrupados por fecha
interface DateGroupedData {
  date: string;
  count: number;
  amount: number;
}

export function AdminDashboard() {
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    totalUsers: 0,
    totalSales: 0,
    totalCVs: 0,
    averageSpend: 0,
    pendingTransactions: 0,
    successfulTransactions: 0,
    cancelledTransactions: 0
  });
  
  // Estado para filtros
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().substring(0, 10),
    end: new Date().toISOString().substring(0, 10)
  });
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  
  // Ordenamiento
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Estado para datos de gráficos
  const [salesByDate, setSalesByDate] = useState<DateGroupedData[]>([]);
  const [usersByDate, setUsersByDate] = useState<DateGroupedData[]>([]);
  const [transactionsByStatus, setTransactionsByStatus] = useState<{name: string, value: number}[]>([]);

  useEffect(() => {
    // Verificar si el usuario es administrador
    const checkAdmin = async () => {
      const isAdmin = localStorage.getItem('isAdmin');
      
      if (!isAdmin) {
        navigate('/admin');
        return;
      }
      
      const { data, error } = await supabase.auth.getSession();
      
      if (error || !data.session) {
        navigate('/admin');
        return;
      }
      
      setSession(data.session);
      setLoading(false);
      
      // Cargar datos iniciales
      fetchMetrics();
      fetchTransactions();
      fetchGraphData();
    };
    
    checkAdmin();
  }, [navigate]);
  
  // Actualizar las métricas y gráficos cuando cambian los filtros de fecha
  useEffect(() => {
    if (!loading && session) {
      // Ya no actualizamos automáticamente al cambiar la fecha
      // fetchMetrics();
      // fetchGraphData();
      // fetchTransactions();
    }
  }, [dateRange]);

  const fetchMetrics = async () => {
    try {
      // Establecer fechas para el filtro
      const fromDate = dateRange.start ? `${dateRange.start}T00:00:00` : null;
      const toDate = dateRange.end ? `${dateRange.end}T23:59:59` : null;
      
      // Obtener el número total de usuarios
      const { data: usersData, error: usersError } = await supabase
        .from('user_coins')
        .select('user_id', { count: 'exact' });
      
      if (usersError) throw usersError;
      
      // Obtener el número total de transacciones exitosas
      let successQuery = supabase
        .from('transactions')
        .select('*', { count: 'exact' })
        .eq('status', 'success');
        
      // Aplicar filtros de fecha si están definidos
      if (fromDate) successQuery = successQuery.gte('created_at', fromDate);
      if (toDate) successQuery = successQuery.lte('created_at', toDate);
      
      const { data: successData, error: successError } = await successQuery;
      if (successError) throw successError;
      
      // Obtener el número total de transacciones pendientes
      let pendingQuery = supabase
        .from('transactions')
        .select('*', { count: 'exact' })
        .eq('status', 'pending');
        
      // Aplicar filtros de fecha si están definidos
      if (fromDate) pendingQuery = pendingQuery.gte('created_at', fromDate);
      if (toDate) pendingQuery = pendingQuery.lte('created_at', toDate);
      
      const { data: pendingData, error: pendingError } = await pendingQuery;
      if (pendingError) throw pendingError;
      
      // Obtener el número total de transacciones canceladas
      let cancelledQuery = supabase
        .from('transactions')
        .select('*', { count: 'exact' })
        .eq('status', 'cancelled');
        
      // Aplicar filtros de fecha si están definidos
      if (fromDate) cancelledQuery = cancelledQuery.gte('created_at', fromDate);
      if (toDate) cancelledQuery = cancelledQuery.lte('created_at', toDate);
      
      const { data: cancelledData, error: cancelledError } = await cancelledQuery;
      if (cancelledError) throw cancelledError;
      
      // Calcular ventas totales
      let totalSales = 0;
      if (successData) {
        totalSales = successData.reduce((sum, transaction) => sum + (transaction.amount || 0), 0);
      }
      
      // Obtener el número total de CVs - CORREGIDO
      let cvsQuery = supabase
        .from('cvs')
        .select('id');
        
      // Aplicar filtros de fecha si están definidos
      if (fromDate) cvsQuery = cvsQuery.gte('created_at', fromDate);
      if (toDate) cvsQuery = cvsQuery.lte('created_at', toDate);
      
      const { data: cvsData, error: cvsError } = await cvsQuery;
      if (cvsError) throw cvsError;
      
      // Calcular el gasto promedio
      const averageSpend = usersData && usersData.length > 0 && totalSales > 0
        ? totalSales / usersData.length
        : 0;
      
      setMetrics({
        totalUsers: usersData?.length || 0,
        totalSales,
        totalCVs: cvsData?.length || 0,
        averageSpend,
        pendingTransactions: pendingData?.length || 0,
        successfulTransactions: successData?.length || 0,
        cancelledTransactions: cancelledData?.length || 0
      });
      
      // Actualizar los datos del gráfico de estado inmediatamente
      setTransactionsByStatus([
        { name: 'Exitosas', value: successData?.length || 0 },
        { name: 'Pendientes', value: pendingData?.length || 0 },
        { name: 'Canceladas', value: cancelledData?.length || 0 }
      ]);
    } catch (error) {
      console.error('Error al obtener métricas:', error);
    }
  };
  
  const fetchTransactions = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('transactions')
        .select(`
          *,
          user:user_id(email)
        `)
        .order(sortField, { ascending: sortDirection === 'asc' });
      
      // Aplicar filtro de fecha
      if (dateRange.start && dateRange.end) {
        query = query
          .gte('created_at', `${dateRange.start}T00:00:00`)
          .lte('created_at', `${dateRange.end}T23:59:59`);
      }
      
      // Aplicar filtro de estado
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }
      
      // Aplicar búsqueda
      if (searchQuery) {
        query = query.or(`transaction_id.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`);
      }
      
      // Obtener el recuento total para la paginación
      const { data: countData, error: countError } = await supabase
        .from('transactions')
        .select('id', { count: 'exact' })
        .eq('status', statusFilter !== 'all' ? statusFilter : 'status')
        .or(searchQuery ? `transaction_id.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%` : 'id.neq.0')
        .gte('created_at', dateRange.start && dateRange.end ? `${dateRange.start}T00:00:00` : '1900-01-01')
        .lte('created_at', dateRange.start && dateRange.end ? `${dateRange.end}T23:59:59` : '2100-01-01');

      if (countError) throw countError;
      setTotalItems(countData.length || 0);
      
      // Aplicar paginación
      const from = (currentPage - 1) * itemsPerPage;
      const to = from + itemsPerPage - 1;
      
      const { data, error } = await query
        .range(from, to);
      
      if (error) throw error;
      
      // Formatear datos para mostrar correos electrónicos de usuarios
      const formattedData = data.map(transaction => ({
        ...transaction,
        user_email: transaction.user?.email
      }));
      
      setTransactions(formattedData);
    } catch (error) {
      console.error('Error al obtener transacciones:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchGraphData = async () => {
    try {
      // Usar el rango de fechas seleccionado
      const fromDate = new Date(dateRange.start);
      const toDate = new Date(dateRange.end);
      
      const { data: salesData, error: salesError } = await supabase
        .from('transactions')
        .select('created_at, amount')
        .eq('status', 'success')
        .gte('created_at', fromDate.toISOString())
        .lte('created_at', toDate.toISOString());
      
      if (salesError) throw salesError;
      
      // Agrupar ventas por fecha
      const salesByDateMap = new Map<string, DateGroupedData>();
      
      if (salesData) {
        salesData.forEach(transaction => {
          const date = transaction.created_at.split('T')[0];
          const existing = salesByDateMap.get(date) || { date, count: 0, amount: 0 };
          
          salesByDateMap.set(date, {
            date,
            count: existing.count + 1,
            amount: existing.amount + (transaction.amount || 0)
          });
        });
      }
      
      // Ordenar por fecha
      const sortedSales = Array.from(salesByDateMap.values()).sort((a, b) => 
        new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      
      setSalesByDate(sortedSales);
      
      // 2. Usuarios registrados por fecha
      const { data: userData, error: userError } = await supabase
        .from('user_coins')
        .select('created_at')
        .gte('created_at', fromDate.toISOString())
        .lte('created_at', toDate.toISOString());
      
      if (userError) throw userError;
      
      // Agrupar usuarios por fecha
      const usersByDateMap = new Map<string, DateGroupedData>();
      
      if (userData) {
        userData.forEach(user => {
          const date = user.created_at.split('T')[0];
          const existing = usersByDateMap.get(date) || { date, count: 0, amount: 0 };
          
          usersByDateMap.set(date, {
            date,
            count: existing.count + 1,
            amount: 0 // No aplica para usuarios
          });
        });
      }
      
      // Ordenar por fecha
      const sortedUsers = Array.from(usersByDateMap.values()).sort((a, b) => 
        new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      
      setUsersByDate(sortedUsers);
      
    } catch (error) {
      console.error('Error al obtener datos para gráficos:', error);
    }
  };
  
  useEffect(() => {
    if (!loading && session) {
      fetchTransactions();
    }
  }, [currentPage, itemsPerPage, sortField, sortDirection, statusFilter, dateRange, searchQuery]);
  
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  
  const handleLogout = async () => {
    await supabase.auth.signOut();
    localStorage.removeItem('isAdmin');
    navigate('/admin');
  };
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  
  const getStatusClass = (status: string) => {
    switch (status) {
      case 'success':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
      case 'cancelled':
        return 'bg-red-500/20 text-red-300 border-red-500/30';
      default:
        return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };
  
  const getTotalPages = () => {
    return Math.ceil(totalItems / itemsPerPage);
  };
  
  // Función para formatear fecha en formato legible
  const formatChartDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getDate()}/${date.getMonth() + 1}`;
  };
  
  // Colores para gráfico de pie
  const COLORS = ['#4ade80', '#facc15', '#f87171'];
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex flex-col justify-center items-center">
        <div className="w-16 h-16 border-4 border-t-purple-500 border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-400">Cargando panel de administración...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Efectos de fondo */}
      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-blue-500/20 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-purple-500/20 rounded-full blur-[80px]" />
      </div>
      
      <div className="relative z-10">
        {/* Barra de navegación */}
        <div className="bg-gray-900/80 backdrop-blur-lg border-b border-purple-500/20 py-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <h1 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400">
                  Admin Dashboard
                </h1>
              </div>
              
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Cerrar sesión</span>
              </button>
            </div>
          </div>
        </div>
        
        {/* Contenido principal */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Filtros globales */}
          <div className="bg-gray-900/60 backdrop-blur-sm p-6 rounded-xl border border-purple-500/30 shadow-lg mb-8">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <h2 className="text-xl font-bold text-white">Filtrar datos por rango de fechas</h2>
              
              <div className="flex flex-wrap items-center gap-2">
                <Calendar className="h-5 w-5 text-purple-400" />
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  className="py-2 px-3 bg-gray-800/60 border border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 text-gray-200"
                />
                <span className="text-gray-500">a</span>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  className="py-2 px-3 bg-gray-800/60 border border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 text-gray-200"
                />
                
                <button
                  onClick={() => {
                    fetchMetrics();
                    fetchTransactions();
                    fetchGraphData();
                  }}
                  className="flex items-center gap-2 py-2 px-4 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                >
                  <RefreshCw className="h-4 w-4" />
                  <span>Actualizar</span>
                </button>
              </div>
            </div>
          </div>
          
          {/* Métricas */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Usuarios totales */}
            <div className="bg-gray-900/60 backdrop-blur-sm p-6 rounded-xl border border-purple-500/30 shadow-lg">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-gray-400 font-medium">Usuarios totales</h3>
                <div className="p-2 bg-purple-900/30 rounded-lg">
                  <Users className="w-5 h-5 text-purple-400" />
                </div>
              </div>
              <div className="flex items-end justify-between">
                <p className="text-3xl font-bold text-white">{metrics.totalUsers}</p>
                <div className="text-sm text-green-400 flex items-center">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  <span>Activos</span>
                </div>
              </div>
            </div>
            
            {/* Ventas totales */}
            <div className="bg-gray-900/60 backdrop-blur-sm p-6 rounded-xl border border-blue-500/30 shadow-lg">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-gray-400 font-medium">Ventas totales</h3>
                <div className="p-2 bg-blue-900/30 rounded-lg">
                  <DollarSign className="w-5 h-5 text-blue-400" />
                </div>
              </div>
              <div className="flex items-end justify-between">
                <p className="text-3xl font-bold text-white">${metrics.totalSales.toLocaleString()}</p>
                <div className="text-sm text-blue-400 flex items-center">
                  <ShoppingCart className="w-4 h-4 mr-1" />
                  <span>{metrics.successfulTransactions} transacciones</span>
                </div>
              </div>
            </div>
            
            {/* CVs generados */}
            <div className="bg-gray-900/60 backdrop-blur-sm p-6 rounded-xl border border-green-500/30 shadow-lg">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-gray-400 font-medium">CVs generados</h3>
                <div className="p-2 bg-green-900/30 rounded-lg">
                  <FileText className="w-5 h-5 text-green-400" />
                </div>
              </div>
              <div className="flex items-end justify-between">
                <p className="text-3xl font-bold text-white">{metrics.totalCVs}</p>
                <div className="text-sm text-green-400 flex items-center">
                  <BarChart3 className="w-4 h-4 mr-1" />
                  <span>Total</span>
                </div>
              </div>
            </div>
            
            {/* Gasto promedio */}
            <div className="bg-gray-900/60 backdrop-blur-sm p-6 rounded-xl border border-yellow-500/30 shadow-lg">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-gray-400 font-medium">Gasto promedio</h3>
                <div className="p-2 bg-yellow-900/30 rounded-lg">
                  <Wallet className="w-5 h-5 text-yellow-400" />
                </div>
              </div>
              <div className="flex items-end justify-between">
                <p className="text-3xl font-bold text-white">${metrics.averageSpend.toFixed(2)}</p>
                <div className="text-sm text-yellow-400 flex items-center">
                  <CreditCard className="w-4 h-4 mr-1" />
                  <span>Por usuario</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Estados de transacciones */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Transacciones exitosas */}
            <div className="bg-gray-900/60 backdrop-blur-sm p-6 rounded-xl border border-green-500/30 shadow-lg">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-gray-400 font-medium">Transacciones exitosas</h3>
                <div className="px-2 py-1 bg-green-500/20 rounded-full text-green-400 text-xs font-medium">
                  Success
                </div>
              </div>
              <p className="text-2xl font-bold text-white mb-2">{metrics.successfulTransactions}</p>
              <div className="w-full bg-gray-800 rounded-full h-2">
                <div 
                  className="bg-green-500 h-2 rounded-full" 
                  style={{ 
                    width: `${Math.round(
                      (metrics.successfulTransactions / 
                      Math.max(1, metrics.successfulTransactions + metrics.pendingTransactions + metrics.cancelledTransactions)) * 100
                    )}%` 
                  }}
                ></div>
              </div>
            </div>
            
            {/* Transacciones pendientes */}
            <div className="bg-gray-900/60 backdrop-blur-sm p-6 rounded-xl border border-yellow-500/30 shadow-lg">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-gray-400 font-medium">Transacciones pendientes</h3>
                <div className="px-2 py-1 bg-yellow-500/20 rounded-full text-yellow-400 text-xs font-medium">
                  Pending
                </div>
              </div>
              <p className="text-2xl font-bold text-white mb-2">{metrics.pendingTransactions}</p>
              <div className="w-full bg-gray-800 rounded-full h-2">
                <div 
                  className="bg-yellow-500 h-2 rounded-full" 
                  style={{ 
                    width: `${Math.round(
                      (metrics.pendingTransactions / 
                      Math.max(1, metrics.successfulTransactions + metrics.pendingTransactions + metrics.cancelledTransactions)) * 100
                    )}%` 
                  }}
                ></div>
              </div>
            </div>
            
            {/* Transacciones canceladas */}
            <div className="bg-gray-900/60 backdrop-blur-sm p-6 rounded-xl border border-red-500/30 shadow-lg">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-gray-400 font-medium">Transacciones canceladas</h3>
                <div className="px-2 py-1 bg-red-500/20 rounded-full text-red-400 text-xs font-medium">
                  Cancelled
                </div>
              </div>
              <p className="text-2xl font-bold text-white mb-2">{metrics.cancelledTransactions}</p>
              <div className="w-full bg-gray-800 rounded-full h-2">
                <div 
                  className="bg-red-500 h-2 rounded-full" 
                  style={{ 
                    width: `${Math.round(
                      (metrics.cancelledTransactions / 
                      Math.max(1, metrics.successfulTransactions + metrics.pendingTransactions + metrics.cancelledTransactions)) * 100
                    )}%` 
                  }}
                ></div>
              </div>
            </div>
          </div>
          
          {/* Sección de gráficos */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center">
              <BarChart3 className="mr-2 h-5 w-5 text-purple-400" />
              Análisis y tendencias
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Gráfico de ventas por día */}
              <div className="bg-gray-900/60 backdrop-blur-sm p-4 rounded-xl border border-purple-500/30 shadow-lg">
                <h3 className="text-gray-300 font-medium mb-2 flex items-center">
                  <LineChartIcon className="w-4 h-4 mr-2 text-purple-400" />
                  Ventas por día
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={salesByDate}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis 
                        dataKey="date"
                        tickFormatter={formatChartDate}
                        stroke="#9ca3af" 
                      />
                      <YAxis stroke="#9ca3af" />
                      <Tooltip 
                        formatter={(value) => [`$${value}`, 'Ventas']}
                        labelFormatter={(label) => `Fecha: ${formatChartDate(label)}`}
                        contentStyle={{ backgroundColor: '#1f2937', borderColor: '#4f46e5', color: '#e5e7eb' }}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="amount" 
                        name="Ventas ($)" 
                        stroke="#8b5cf6" 
                        strokeWidth={2}
                        dot={{ r: 4, fill: '#8b5cf6' }}
                        activeDot={{ r: 6, fill: '#a78bfa' }} 
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              
              {/* Gráfico de nuevos usuarios por día */}
              <div className="bg-gray-900/60 backdrop-blur-sm p-4 rounded-xl border border-blue-500/30 shadow-lg">
                <h3 className="text-gray-300 font-medium mb-2 flex items-center">
                  <Users className="w-4 h-4 mr-2 text-blue-400" />
                  Nuevos usuarios por día
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={usersByDate}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={formatChartDate}
                        stroke="#9ca3af" 
                      />
                      <YAxis stroke="#9ca3af" />
                      <Tooltip 
                        formatter={(value) => [`${value}`, 'Usuarios']}
                        labelFormatter={(label) => `Fecha: ${formatChartDate(label)}`}
                        contentStyle={{ backgroundColor: '#1f2937', borderColor: '#3b82f6', color: '#e5e7eb' }}
                      />
                      <Legend />
                      <Bar 
                        dataKey="count" 
                        name="Nuevos usuarios" 
                        fill="#3b82f6" 
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
            
            {/* Gráfico de transacciones por estado */}
            <div className="bg-gray-900/60 backdrop-blur-sm p-4 rounded-xl border border-green-500/30 shadow-lg">
              <h3 className="text-gray-300 font-medium mb-2 flex items-center">
                <PieChart className="w-4 h-4 mr-2 text-green-400" />
                Distribución de transacciones por estado
              </h3>
              <div className="h-72 flex items-center justify-center">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie
                      data={transactionsByStatus}
                      cx="50%"
                      cy="50%"
                      innerRadius={70}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      nameKey="name"
                      label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {transactionsByStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => [`${value}`, 'Transacciones']}
                      contentStyle={{ backgroundColor: '#1f2937', borderColor: '#10b981', color: '#e5e7eb' }}
                    />
                    <Legend 
                      formatter={(value) => (
                        <span style={{ color: '#e5e7eb' }}>{value}</span>
                      )}
                    />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          
          {/* Tabla de transacciones */}
          <div className="bg-gray-900/60 backdrop-blur-sm rounded-xl border border-purple-500/30 shadow-lg overflow-hidden mb-8">
            <div className="p-6 border-b border-gray-800">
              <h2 className="text-xl font-bold text-white mb-4">Historial de transacciones</h2>
              
              {/* Filtros */}
              <div className="flex flex-col md:flex-row gap-4 mb-4">
                {/* Búsqueda */}
                <div className="relative flex-grow">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-gray-500" />
                  </div>
                  <input
                    type="text"
                    placeholder="Buscar por ID o descripción..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 bg-gray-800/60 border border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 text-gray-200 placeholder-gray-500"
                  />
                </div>
                
                {/* Filtro de estado */}
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-gray-500" />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="py-2 px-3 bg-gray-800/60 border border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 text-gray-200"
                  >
                    <option value="all">Todos los estados</option>
                    <option value="success">Exitosas</option>
                    <option value="pending">Pendientes</option>
                    <option value="cancelled">Canceladas</option>
                  </select>
                </div>
              </div>
            </div>
            
            {/* Tabla */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-800/50">
                  <tr>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('transaction_id')}
                    >
                      <div className="flex items-center gap-1">
                        <span>ID</span>
                        {sortField === 'transaction_id' && (
                          sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('user_id')}
                    >
                      <div className="flex items-center gap-1">
                        <span>Usuario</span>
                        {sortField === 'user_id' && (
                          sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('amount')}
                    >
                      <div className="flex items-center gap-1">
                        <span>Monto</span>
                        {sortField === 'amount' && (
                          sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('vcoins_amount')}
                    >
                      <div className="flex items-center gap-1">
                        <span>Monedas</span>
                        {sortField === 'vcoins_amount' && (
                          sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center gap-1">
                        <span>Estado</span>
                        {sortField === 'status' && (
                          sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('created_at')}
                    >
                      <div className="flex items-center gap-1">
                        <span>Fecha</span>
                        {sortField === 'created_at' && (
                          sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('description')}
                    >
                      <div className="flex items-center gap-1">
                        <span>Descripción</span>
                        {sortField === 'description' && (
                          sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {transactions.length > 0 ? (
                    transactions.map((transaction) => (
                      <tr key={transaction.id} className="bg-gray-900/40 hover:bg-gray-800/40 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-300">
                          {transaction.transaction_id.substring(0, 8)}...
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          {transaction.user_email || 'Usuario desconocido'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          ${transaction.amount}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {transaction.vcoins_amount}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`px-2 py-1 rounded-full text-xs font-medium text-center inline-block ${getStatusClass(transaction.status)}`}>
                            {transaction.status}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          {formatDate(transaction.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400 truncate max-w-[200px]">
                          {transaction.description}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="px-6 py-16 text-center text-gray-500">
                        No se encontraron transacciones con los filtros aplicados
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Paginación */}
            <div className="px-6 py-4 bg-gray-900/40 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-400">
                Mostrando <span className="font-medium text-gray-300">{transactions.length}</span> de{' '}
                <span className="font-medium text-gray-300">{totalItems}</span> transacciones
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded-md bg-gray-800 text-gray-400 disabled:opacity-50"
                >
                  Anterior
                </button>
                
                <div className="px-4 py-1 rounded-md bg-purple-600/20 text-purple-300 border border-purple-500/30">
                  {currentPage} de {getTotalPages() || 1}
                </div>
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, getTotalPages()))}
                  disabled={currentPage >= getTotalPages()}
                  className="px-3 py-1 rounded-md bg-gray-800 text-gray-400 disabled:opacity-50"
                >
                  Siguiente
                </button>
                
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  className="ml-2 px-2 py-1 bg-gray-800/60 border border-gray-700 rounded-lg text-gray-400"
                >
                  <option value={10}>10 / página</option>
                  <option value={25}>25 / página</option>
                  <option value={50}>50 / página</option>
                  <option value={100}>100 / página</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard; 